/**
 * Отправка профиля языка на сервер.
 *
 * Профиля языка на сервере не заводил никто. `PUT /v1/workspace/profiles`
 * написан с самого начала, CORS для него починен на этапе X — и не вызван ни
 * разу за проект. Из-за этого сервер знал об аккаунте всё, а о человеке
 * ничего: `rating` в вердикте контрольного теста всегда приходил `null`,
 * сохранение навыка не засчитывалось никогда, отложенная проверка не
 * наступала, а согласие на таблицу лидеров получало 404.
 *
 * Профиль отправляется как состояние, а не как событие, и потому мимо очереди
 * синхронизации (AD-314). Очередь хранит то, что случилось: итог занятия
 * верен и через сутки в офлайне. Профиль верен только пока он текущий —
 * отлежавшаяся в очереди раскладка перезаписала бы на сервере ту, которую
 * человек выбрал после. Взамен отправка повторяется сама: пока она не удалась,
 * подпись отправленного не запоминается, и следующая же перемена в
 * пространстве — или следующий запуск приложения — пробует снова.
 */

import { useEffect, useRef } from 'react';
import type { ProfilePayload } from '@type2x/feature-sync';
import type { Workspace } from '@type2x/feature-personal-workspace';

import type { Runtime } from '../runtime.js';

/** Собирает то, что сервер знает о профиле языка. */
function toPayload(profile: Workspace['profiles'][string]): ProfilePayload {
  return {
    language: profile.language,
    layoutId: profile.keyboard.layoutId,
    baseline:
      profile.baseline === null
        ? null
        : {
            wpm: profile.baseline.wpm,
            cpm: profile.baseline.cpm,
            accuracy: profile.baseline.accuracy,
          },
  };
}

/**
 * Подпись отправленного.
 *
 * Занятие меняет пространство после каждого прогона, а профиль на сервере —
 * три поля из него. Подпись отделяет перемену, которую серверу надо знать, от
 * той, которая его не касается: иначе запрос уходил бы после каждого занятия.
 */
function signatureOf(payloads: readonly ProfilePayload[]): string {
  return JSON.stringify(payloads);
}

/**
 * Держит профили языков на сервере в согласии с пространством.
 *
 * @param runtime зависимости приложения;
 * @param workspace текущее пространство; `null` — ещё не загружено.
 */
export function useProfileSync(runtime: Runtime, workspace: Workspace | null): void {
  const sent = useRef<string | null>(null);

  useEffect(() => {
    if (workspace === null) return;

    const payloads = Object.values(workspace.profiles).map(toPayload);
    const signature = signatureOf(payloads);
    if (payloads.length === 0 || signature === sent.current) return;

    void (async () => {
      for (const payload of payloads) {
        const outcome = await runtime.sync.upsertProfile(payload);
        // Гость, обрыв связи, отказ сервера — подпись не запоминается, и
        // следующая попытка случится сама. Появление аккаунта меняет
        // пространство, и она случится сразу после него.
        if (outcome.kind !== 'accepted') return;
      }

      sent.current = signature;
    })();
  }, [runtime, workspace]);
}
